"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var extend_1 = __importDefault(require("extend"));
var loglevel_1 = __importDefault(require("loglevel"));
var TrackerUtil_1 = __importDefault(require("./tracker/TrackerUtil"));
var GoogleAnalytics_1 = __importDefault(require("./tracker/GoogleAnalytics"));
var MicrosoftUniversalEventTracking_1 = __importDefault(require("./tracker/MicrosoftUniversalEventTracking"));
var FacebookPixel_1 = __importDefault(require("./tracker/FacebookPixel"));
var LinkedInInsight_1 = __importDefault(require("./tracker/LinkedInInsight"));
/**
 *
 */
var Analytics = /** @class */ (function () {
    function Analytics(options) {
        var _this = this;
        this._logger = null;
        Analytics.instance = this;
        // Options
        this.options = extend_1.default(true, {}, Analytics.defaultOptions, options);
        options = this.options;
        // Add hostname of the site to the list.
        options.hostnames.push(window.location.hostname);
        if (window.location.hostname.toLowerCase().indexOf("www.") === 0) {
            options.hostnames.push(window.location.hostname.substring(4));
        }
        else {
            options.hostnames.push("www." + window.location.hostname);
        }
        // Setup logger - We call getLogger() here to ensure it is configured before we start adding trackers which (by default) use the same logger name.
        var logger = this.getLogger();
        // TODO Add more logging throughout all code. For example, when a new tracker is added, the current configuration object.
        // === Trackers ===
        this.trackers = new Array();
        var addTrackerForOption = function (platformName, getInstance) {
            var trackerOptionPropertyName = platformName.charAt(0).toLowerCase() + platformName.slice(1); // E.g.: "GoogleAnalytics" to "googleAnalytics"
            var trackerOpt = _this.options.trackers[trackerOptionPropertyName];
            var tracker;
            if (trackerOpt !== undefined && trackerOpt !== null) {
                // Type: boolean
                if (typeof trackerOpt === "boolean") {
                    if (trackerOpt === true) {
                        tracker = getInstance();
                        if (tracker !== null) {
                            _this.addTracker(tracker);
                        }
                        else {
                            logger.warn("No instance for \"" + trackerOptionPropertyName + "\" could be found.");
                        }
                    }
                }
                // Type: BasicTracker
                else if (typeof trackerOpt.platformName === "string") {
                    _this.addTracker(trackerOpt);
                    tracker = trackerOpt;
                }
                // Type: BasicTrackerOptions
                else {
                    tracker = getInstance(trackerOpt);
                    if (tracker !== null) {
                        _this.addTracker(tracker);
                    }
                    else {
                        logger.warn("No instance for \"" + trackerOptionPropertyName + "\" could be found or created.", trackerOpt);
                    }
                }
            }
            // Update the property in the options to point to the initialized tracker.
            if (tracker !== null && tracker !== undefined) {
                _this.options.trackers[trackerOptionPropertyName] = tracker;
            }
        };
        addTrackerForOption(GoogleAnalytics_1.default.platformName, GoogleAnalytics_1.default.getInstance);
        addTrackerForOption(MicrosoftUniversalEventTracking_1.default.platformName, MicrosoftUniversalEventTracking_1.default.getInstance);
        addTrackerForOption(FacebookPixel_1.default.platformName, FacebookPixel_1.default.getInstance);
        addTrackerForOption(LinkedInInsight_1.default.platformName, LinkedInInsight_1.default.getInstance);
        // Setup custom tracking
        var setupFeature = function (featureKey, setupFunction) {
            var feature = _this.options.features[featureKey]; // boolean|BasicFeatureOptions
            if (feature !== undefined && feature !== null && feature !== false && feature.platforms !== false) {
                if (feature === true) {
                    // If feature is a boolean of true, then we set the child platform options to the default list.
                    var featureDefaults = Analytics.defaultOptions.features[featureKey];
                    _this.options.features[featureKey] = {
                        platforms: featureDefaults.platforms
                    };
                }
                setupFunction.apply(_this);
            }
            else {
                _this.options.features[featureKey] = false;
            }
        };
        setupFeature("mirrorStrayHitsToGoogleAnalyticsToOtherTrackers", this.setupMirrorStrayHitsToGoogleAnalyticsToOtherTrackers);
        setupFeature("nonHtmlAnchorClickTracking", this.setupNonHtmlAnchorClickTracking);
        setupFeature("outboundFormSubmissionTracking", this.setupOutboundFormSubmissionTracking);
        setupFeature("errorTracking", this.setupErrorTracking);
        // Make accessible - For example, window["@plaudit/analytics"] will call Analytics.getInstance().
        if (this.options.globalInstanceKey !== null && this.options.globalInstanceKey.length !== 0) {
            Object.defineProperty(window, this.options.globalInstanceKey, { get: Analytics.getInstance });
        }
        // Log information
        logger.debug("Analytics instance initialized.", "Options: ", this.options, "Trackers: ", this.trackers);
    }
    Analytics.getInstance = function (options) {
        if (Analytics.instance === undefined) {
            new Analytics(options);
        }
        else if (options !== undefined) {
            throw new Error("Instantiation failed: Cannot reconfigure Analytics. An instance was already created. To obtain a reference call getInstance() with no arguments.");
        }
        return Analytics.instance;
    };
    /**
     * Add a child tracker to this instance of Analytics.
     */
    Analytics.prototype.addTracker = function (tracker) {
        this.trackers.push(tracker);
    };
    /**
     * Returns an array of trackers registered with this Analytics instance.
     * The returned array is a shallow copy so changes to the array will not effect the list used by this instance of Analytics instance.
     * @returns {BasicTracker[]}
     */
    Analytics.prototype.getTrackers = function (platformName) {
        if (platformName === undefined || platformName === null || platformName.length === 0) {
            return this.trackers.slice(0);
        }
        return this.trackers.filter(function (tracker) { return tracker.platformName === platformName; });
    };
    /**
     * Same as getTrackers() except it returns the first result or null.
     * @returns {BasicTracker|null}
     */
    Analytics.prototype.getTracker = function (platformName) {
        if (platformName === undefined || platformName === null || platformName.length === 0) {
            if (this.trackers.length > 0) {
                return this.trackers[0];
            }
            return null;
        }
        var filteredTrackers = this.trackers.filter(function (tracker) { return tracker.platformName === platformName; });
        if (filteredTrackers.length > 0) {
            return filteredTrackers[0];
        }
        return null;
    };
    Analytics.prototype.isPlatformIncluded = function (platformName, platforms) {
        return platforms === undefined || platforms === true || platforms.indexOf(platformName) !== -1;
    };
    /**
     * @public
     * @param {string} pagePath - The path portion of a URL.
     * @param {string=} title - The title of the page (e.g. homepage)
     * @param {platforms[]=} title - An array of platforms to limit the reporting too. E.g. [GoogleAnalytics.platformName,MicrosoftUniversalEventTracking.platformName]
     */
    Analytics.prototype.trackPageview = function (pagePath, title, platforms) {
        // Call `trackPageview()` on each registered tracker
        var tracker;
        for (var i = 0; i < this.trackers.length; i++) {
            tracker = this.trackers[i];
            if (this.isPlatformIncluded(tracker.platformName, platforms)
                && TrackerUtil_1.default.isBasicPageviewTracker(tracker)) {
                tracker.trackPageview(pagePath, title);
            }
        }
        // Log
        this.getLogger().info("Analytics.trackPageview:", {
            page: pagePath,
            title: title,
            platforms: platforms
        });
    };
    /**
     * @param {string} category -
     * @param {stringstring[]} action -
     * @param {string|number|string[]} label - Optional.
     * @param {number} value - Optional
     * @param {boolean} nonInteraction - Optional
     * @param {platforms[]=} platforms - An array of platforms to limit the reporting too. E.g. [GoogleAnalytics.platformName,MicrosoftUniversalEventTracking.platformName]
     */
    Analytics.prototype.trackEvent = function (category, action, label, value, nonInteraction, platforms) {
        action = TrackerUtil_1.default.eventActionToString(action);
        label = TrackerUtil_1.default.eventLabelToString(label);
        // Call `trackEvent()` on each registered tracker
        var tracker;
        for (var i = 0; i < this.trackers.length; i++) {
            tracker = this.trackers[i];
            if (this.isPlatformIncluded(tracker.platformName, platforms)
                && TrackerUtil_1.default.isBasicEventTracker(tracker)) {
                tracker.trackEvent(category, action, label, value, nonInteraction);
            }
        }
        // Log
        this.getLogger().info("Analytics.trackEvent:", {
            category: category,
            action: action,
            label: label,
            value: value,
            nonInteraction: nonInteraction,
            platforms: platforms
        });
    };
    /**
     * Setup mirroring of Google Analytics stray hits to other trackers.
     * This only applies to stray hits to the instance of GoogleAnalytics setup during the initialization
     * of this class, Analytics. If you want to mirror stray hist from other instances
     * of GoogleAnalytics, then you must call addTrackerToMirrorStrayHits() directly
     * on the desired instance of GoogleAnalytics.
     */
    Analytics.prototype.setupMirrorStrayHitsToGoogleAnalyticsToOtherTrackers = function () {
        if (this.options.trackers.googleAnalytics !== undefined && this.options.trackers.googleAnalytics !== null
            && typeof this.options.trackers.googleAnalytics.addTrackerToMirrorStrayHits === "function") {
            var gaTracker = this.options.trackers.googleAnalytics;
            var tracker = void 0;
            for (var i = 0; i < this.trackers.length; i++) {
                tracker = this.trackers[i];
                gaTracker.addTrackerToMirrorStrayHits(tracker);
                // Note: We do not need to ensure tracker !== gaTracker because addTrackerToMirrorStrayHits() does that check.
            }
        }
    };
    /**
     * Setup tracking of anchor link clicks for certain types (e.g. outbound, emails, telephone, etc.)
     */
    Analytics.prototype.setupNonHtmlAnchorClickTracking = function () {
        var _this = this;
        var onAnchorClick = function (ev) {
            var target = ev.currentTarget;
            if (!(target instanceof HTMLAnchorElement)) {
                return;
            }
            var element = target;
            var category = _this.determineLinkEventCategory(element);
            if (category !== null) {
                var nonInteract = category === "Outbound" ? true : false; // TODO: Why are outbounds not interactive? This is how the legacy code was but that does not make immediate sense to me. Should be given more thought.
                if (category !== undefined && category !== null) {
                    _this.trackEvent(category + " Link", "Click", element.href, 0, nonInteract);
                }
            }
        };
        // Tracks links that Google Analytics normally does not track
        var anchors = document.getElementsByTagName("a");
        for (var i = 0; i < anchors.length; i++) {
            anchors[i].addEventListener("click", onAnchorClick, false);
        }
    };
    /**
     * Setup tracking of outbound form form submissions
     */
    Analytics.prototype.setupOutboundFormSubmissionTracking = function () {
        var _this = this;
        var onFormSubmit = function (ev) {
            var target = ev.target || ev.srcElement;
            if (!(target instanceof Element) || target.tagName === undefined || target.tagName.toUpperCase() !== "FORM") {
                return;
            }
            var element = target; // Do not use HTMLFormEelement to get 'action' property because IE doe snot support that.
            var action = element.getAttribute("action");
            // Ignore form submission that are not to other domains
            if (action === null || action === undefined || action.indexOf("http") === -1) {
                return;
            }
            // Check if hostname is in the list of "local" hostnames
            for (var i = 0; i < _this.options.hostnames.length; i++) {
                if (action.indexOf("//" + _this.options.hostnames[i]) !== -1) {
                    return;
                }
            }
            // Track event
            _this.trackEvent("Outbound Form Submissions", "Submit", action);
        };
        document.documentElement.addEventListener("submit", onFormSubmit, false);
    };
    /**
     * Setup the tracking of errors (e.g. JS errors, image loading issues, etc.)
     */
    Analytics.prototype.setupErrorTracking = function () {
        var _this = this;
        var trackError = function (ev) {
            var target = ev.target;
            if (target instanceof HTMLImageElement
                && target.src.indexOf("bat.bing.com") !== -1) {
                // TODO Consider changing logic to the inverse and to on report if the domain is part of the list.... Maybe. Catching errors of external code would be good, it is just that MS seems to return data that is not not always an image.
                return;
            }
            _this.getLogger().debug("Error object being tracked: ", ev);
            var actionValue = new Array();
            // Filename and location in file
            if (ev.filename !== undefined) {
                var location_1 = ev.filename;
                if (ev.lineno !== undefined) {
                    location_1 += ":" + ev.lineno;
                    if (ev.colno !== undefined) {
                        location_1 += ":" + ev.colno;
                    }
                }
                actionValue.push(location_1);
            }
            if (target !== undefined && target !== null) {
                // Tag/Node Name
                var nodeName = target.nodeName || target.tagName;
                if (nodeName !== undefined) {
                    actionValue.push("Target: " + nodeName);
                }
                // Key element property
                if (target instanceof HTMLImageElement) {
                    actionValue.push("HTMLImageElement.src: " + target.src);
                }
                else if (target instanceof HTMLVideoElement) {
                    actionValue.push("HTMLVideoElement.src: " + target.src);
                }
                else if (target instanceof HTMLFormElement) {
                    actionValue.push("HTMLFormElement.action: " + target.action);
                }
                else if (target instanceof HTMLLinkElement) {
                    actionValue.push("HTMLLinkElement.href: " + target.href);
                }
            }
            // Track the info
            _this.trackEvent("Tracked Errors", actionValue, (ev.error && ev.error.stack) || ev.message || Analytics.emptyValue, 0, true, _this.options.features.errorTracking.platforms);
        };
        // Replay any stored error events during load and remove listener
        if (window.__analyticsTrackError !== undefined) {
            var loadErrorEvents = window.__analyticsTrackError && window.__analyticsTrackError.q || [];
            for (var i = 0; i < loadErrorEvents.length; i++) {
                trackError(loadErrorEvents[i]);
            }
            window.removeEventListener("error", window.__analyticsTrackError, true);
            window.__analyticsTrackError = undefined;
        }
        // Add a new listener to track error events
        // Note are using "capture" by setting the last argument to "true". This is on purpose! Otherwise we won't get reports of image load errors and the like.
        window.addEventListener("error", trackError, true);
    };
    /**
     * @private
     * Return of null would likely indicate an internal link that is html
     */
    Analytics.prototype.determineLinkEventCategory = function (element) {
        if (element.href === undefined || element.href === null || element.href.length === 0) {
            return null;
        }
        var protocol = element.protocol === undefined ? "" : element.protocol.toLowerCase();
        var hostname = element.hostname === undefined ? "" : element.hostname.toLowerCase();
        var pathname = element.pathname === undefined ? "" : element.pathname.toLowerCase();
        if (protocol.indexOf("http") === 0) {
            if (this.isOutboundHostname(hostname)) {
                return "Outbound";
            }
            else {
                var extIndexOf = pathname.lastIndexOf(".");
                if (extIndexOf !== -1) {
                    var ext = pathname.substring(extIndexOf + 1);
                    if (ext !== "html"
                        && ext !== "htm"
                        && ext !== "php"
                        && ext !== "asp") {
                        return "Download";
                        // TODO, should this be renamed away from "Download"? Because it is not necassarily a download. Its more just not a standard HTML file.
                    }
                }
            }
        }
        else if (protocol === "mailto:") {
            return "Email";
        }
        else if (protocol === "tel:") {
            return "Telephone";
        }
        else if (protocol === "ftp:") {
            return "FTP";
        }
        else if (protocol === "file:") {
            return "File";
        }
        return null;
    };
    Analytics.prototype.isOutboundHostname = function (hostname) {
        return hostname.length !== 0 && !this.inArray(hostname, this.options.hostnames);
    };
    Analytics.prototype.inArray = function (item, list) {
        for (var i = 0; i < list.length; i++) {
            if (list[i] === item) {
                return true;
            }
        }
        return false;
    };
    /**
     */
    Analytics.prototype.getLogger = function () {
        if (this._logger === null) {
            this._logger = loglevel_1.default.getLogger(this.getLoggerName());
            if (this.options.logger.defaultLevel !== undefined && this.options.logger.defaultLevel !== null) {
                this._logger.setDefaultLevel(this.options.logger.defaultLevel);
            }
        }
        return this._logger;
    };
    /**
     * Get the name of the logger for this instance
     */
    Analytics.prototype.getLoggerName = function (platformName) {
        var loggerName = this.options.logger.name;
        if (platformName !== undefined && platformName !== null) {
            loggerName += "-" + platformName;
        }
        return loggerName;
    };
    /**
     * Get the default logging level for a platform. This is set through the options
     * object passed to Analytics.getInstance()
     */
    Analytics.prototype.getLoggerDefaultLevel = function (platformName) {
        var defaultLevel;
        if (platformName !== undefined && platformName !== null) {
            defaultLevel = this.options.logger.defaultLevelForPlatforms;
        }
        else {
            defaultLevel = this.options.logger.defaultLevel;
        }
        return defaultLevel;
    };
    Analytics.defaultOptions = {
        globalInstanceKey: "@plaudit/analytics",
        trackers: {
            googleAnalytics: true,
            microsoftUniversalEventTracking: true,
            facebookPixel: true,
            linkedInInsight: true
        },
        features: {
            mirrorStrayHitsToGoogleAnalyticsToOtherTrackers: {
                platforms: [GoogleAnalytics_1.default.platformName, MicrosoftUniversalEventTracking_1.default.platformName, FacebookPixel_1.default.platformName]
            },
            nonHtmlAnchorClickTracking: {
                platforms: [GoogleAnalytics_1.default.platformName, MicrosoftUniversalEventTracking_1.default.platformName, FacebookPixel_1.default.platformName]
            },
            outboundFormSubmissionTracking: {
                platforms: [GoogleAnalytics_1.default.platformName, MicrosoftUniversalEventTracking_1.default.platformName, FacebookPixel_1.default.platformName]
            },
            errorTracking: {
                platforms: [GoogleAnalytics_1.default.platformName]
            }
        },
        hostnames: [],
        logger: {
            name: "@plaudit/analytics",
            defaultLevel: loglevel_1.default.levels.SILENT,
            defaultLevelForPlatforms: loglevel_1.default.levels.SILENT
        }
    };
    Analytics.emptyValue = "(not set)"; // Standard value to use when a dimension is not set / empty.
    Analytics.valueDelimiter = " • "; // Standard value to use as a delimiter. For example, when joining an array to create a string to report as a label: label.join(Analytics.valueDelimiter)
    return Analytics;
}());
exports.default = Analytics;
;
